import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Permission, PermissionList, RoleSetting } from '@wb/core/interfaces/account-permission';
import { AuthService } from '@wb/core/services/auth.service';
import { DisplayService } from '@wb/core/services/display.service';
import { RoutingService } from '@wb/core/services/routing.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-select-role-table',
  templateUrl: './select-role-table.component.html',
  styleUrls: ['./select-role-table.component.sass'],
})
export class SelectRoleTableComponent implements OnInit {
  @Input() userUid: string | null | undefined;

  @Input() onSave: Observable<void> | undefined;

  @Input() navPath: string | undefined;

  @Output() emitLoadingEvent = new EventEmitter<boolean>();

  @Output() emitNameEvent = new EventEmitter<string>();

  @Output() isValidSubject = new EventEmitter<boolean>();

  @Output() closeDialogEvent = new EventEmitter<boolean>();

  @Output() emitSaveDone = new EventEmitter<Promise<boolean>[] | undefined[]>();

  isLoadingData: boolean | undefined;

  activePermissions: RoleSetting[] = [];

  saveSubscription: Subscription | undefined;

  roleForm: FormGroup = new FormGroup({
    uid: new FormControl(''),
  });

  initialValues: any = [];

  accountUid = '';

  activeAccountPermission: Permission = this.auth.defaultPermission;

  isExternalRole: Boolean = false;

  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly routing: RoutingService,
    private readonly route: ActivatedRoute,
    private auth: AuthService,
    public readonly displayService: DisplayService,
  ) {}

  async ngOnInit() {
    this.isLoadingData = true;
    this.emitLoadingEvent.emit(true);
    this.roleForm = this.fb.group({});
    this.accountUid = await this.routing.getSelectedAccountUid(this.route);
    this.auth.externalRole$.pipe(take(1)).subscribe((externalRole) => {
      this.isExternalRole = externalRole;
    });

    if (this.onSave) {
      this.saveSubscription = this.onSave.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.changeRole();
      });
    }

    this.auth.userPermission.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response: PermissionList | undefined) => {
        if (response) {
          this.activeAccountPermission = response.users;
          if (this.userUid) {
            this.fetchEditData(this.userUid);
          }
        }
      },
    });

    // Validate the form requiring the default account to have an assigned role
    this.roleForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.isValidSubject.next(this.roleForm.get(this.accountUid)?.value?.uid);
    });
  }

  fetchEditData(userUid: string) {
    const adminUid = this.auth.getUserUid;
    if (!adminUid) {
      return;
    }
    this.auth.queryPermissionsAndOptionsByUser(userUid, adminUid);
    this.auth.userPermissionAndOptions.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response) => {
        if (response) {
          this.initialValues = response.options;
          let options = response.options;
          this.activePermissions = [];

          for (let i in options) {
            this.activePermissions.push({
              title: options[i].permissionTitle,
              uid: options[i].permissionUid,
              accountTitle: options[i].accountTitle,
              accountUid: options[i].accountUid,
              options: options[i].options,
              enabled: options[i].accountUid === this.accountUid ? true : options[i].enabled,
            });

            this.roleForm.addControl(
              options[i].accountUid,
              new FormGroup({
                uid: new FormControl(options[i].permissionUid),
                accountUid: new FormControl(options[i].accountUid),
              }),
            );
          }

          this.isLoadingData = false;
          this.emitLoadingEvent.emit(false);
        }
      },
    });
  }

  async changeRole() {
    let createInputs = [];
    let editInputs = [];
    let deleteInputs = [];

    let index = 0;
    for (let i in this.roleForm.value) {
      let initalState = this.initialValues[index];

      if (this.roleForm.value[i].uid) {
        if (!initalState.enabled) {
          createInputs.push({
            userUid: this.userUid,
            accountUid: this.roleForm.value[i].accountUid,
            permissionUid: this.roleForm.value[i].uid,
          });
        } else if (initalState.permissionUid !== this.roleForm.value[i].uid) {
          editInputs.push({
            userUid: this.userUid,
            accountUid: this.roleForm.value[i].accountUid,
            permissionUid: this.roleForm.value[i].uid,
          });
        }
      } else {
        if (initalState.enabled) {
          deleteInputs.push({
            userUid: this.userUid,
            accountUid: this.roleForm.value[i].accountUid,
          });
        }
      }
      ++index;
    }

    this.auth.editUserRole(createInputs, editInputs, deleteInputs);
    this.auth.editRoleDone.pipe(takeUntil(this.destroyed$)).subscribe({
      next: async (response) => {
        if (response) {
          this.emitSaveDone.emit(response);
        }
      },
    });
  }
}
